import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LinkAsButton } from "@/components/LinkAsButton";
import { EntityListPage } from "@/components/entity-page/EntityListPage";
import { getAgentBlueprints } from "@/lib/formOptions";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/agent-blueprints/")({
  component: AgentBlueprintsIndexPage,
});

interface AgentBlueprintMetadata {
  typeId?: string;
  schedulingProvider?: string;
}

function parseMetadata(blueprint: FormOption): AgentBlueprintMetadata {
  const raw = typeof blueprint.metadata === "string" ? safeParse(blueprint.metadata) : blueprint.metadata;
  return (raw ?? {}) as AgentBlueprintMetadata;
}

function safeParse(raw: string): Record<string, unknown> {
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

// Exported for testing
export function AgentBlueprintsIndexPage() {
  const [blueprints, setBlueprints] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getAgentBlueprints()
      .then((options) => {
        if (!active) return;
        setBlueprints(options);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching agent blueprints:", err);
        setError("Failed to load agent blueprints.");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <EntityListPage
      title="Agent Blueprints"
      description="Reusable agent configuration templates - deploy a new agent from one to skip re-entering the system prompt, model, and voice settings from scratch."
      items={blueprints}
      loading={loading}
      error={error}
      getItemKey={(blueprint) => blueprint.id}
      gridClassName="grid grid-cols-1 gap-4"
      renderItem={(blueprint) => {
        const metadata = parseMetadata(blueprint);
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle>{blueprint.label}</CardTitle>
                  <CardDescription>
                    {blueprint.description || `Slug: ${blueprint.value}`}
                    {metadata.typeId && ` • Type: ${metadata.typeId}`}
                    {metadata.schedulingProvider &&
                      metadata.schedulingProvider !== "none" &&
                      ` • Scheduling: ${metadata.schedulingProvider}`}
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/dashboard/agent-blueprints/$id" params={{ id: blueprint.id }}>
                    Edit
                  </Link>
                </Button>
              </div>
            </CardHeader>
          </Card>
        );
      }}
      createHref="/dashboard/agent-blueprints/new"
      createLabel="Create Blueprint"
      emptyMessage="No blueprints yet. Create one to give admins a starting point for new agents."
      headerActions={
        <LinkAsButton to="/dashboard/settings" variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </LinkAsButton>
      }
    />
  );
}
